import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import DeleteModal from "components/DeleteModal/DeleteModal";
import { PlanFeatureDialogForm } from "components/FormDialog/PlanFeatureDialogForm";
import Loading from "components/Loader/Loading";
import DynamicTable from "components/Tables/DynamicTable";
import DashboardMenu from "containers/Admin/DashboardPage/DashboardMenu";
import { formatDate, useAppDispatch, useAppSelector, useDebounce } from "hooks/hooks";
import { ListCollapse } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { addFeature, adminGetAllFeatures, deleteFeature } from "redux/features/Admin/PlanFeature/planFeatureSlice";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import ContactDetailsPage from "./ContactDetailsPage";
import PlanFeatureDetail from "./PlanFeatureDetail";

export interface PlanFeaturesProps {
  className?: string;
}

export interface PlanFeature {
  id: number;
  description: string;
  createdAt?: string;
}

interface ModalState {
  isDeleteModalOpen: boolean;
  isViewModalOpen: boolean;
  isAddModalOpen: boolean;
  selectedItem: PlanFeature | null;
  loading: boolean;
}

const PlanFeatures: FC<PlanFeaturesProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const {
    features: featuresList,
    totalCount,
    totalPages,
    currentPage,
    loading,
  } = useAppSelector((state) => ({
    features: state.planFeature.allFeatures.features || [],
    totalCount: state.planFeature.allFeatures?.totalCount || 0,
    totalPages: state.planFeature.allFeatures?.totalPages || 0,
    currentPage: state.planFeature.allFeatures?.currentPage || 1,
    loading: state.planFeature?.loading || "idle",
  }));

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({
    loading: false,
    isAddModalOpen: false,
    isDeleteModalOpen: false,
    isViewModalOpen: false,
    selectedItem: null,
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const fetchData = async () => {
      setIsSearchLoading(true);
      await dispatch(adminGetAllFeatures({ page, searchTerm: debouncedSearchTerm }));
      setIsSearchLoading(false);
    };
    fetchData();
  }, [dispatch, page, debouncedSearchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handlePageChange = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const handleDeleteModalOpen = (contact: PlanFeature) => {
    setModalState({ ...modalState, selectedItem: contact, isDeleteModalOpen: true });
  };

  const handleViewModalOpen = (contact: PlanFeature) => {
    setModalState({ ...modalState, selectedItem: contact, isViewModalOpen: true });
  };

  const handleModalClose = () => {
    setModalState({
      ...modalState,
      isDeleteModalOpen: false,
      isViewModalOpen: false,
      selectedItem: null,
    });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.selectedItem) {
      try {
        setModalState((prev) => ({ ...prev, loading: true }));

        const result = await dispatch(deleteFeature(modalState.selectedItem.id)).unwrap();

        //toast.success(result.message || "Suppression réussie");

        setModalState({
          ...modalState,
          isDeleteModalOpen: false,
          loading: false,
          selectedItem: null,
        });

        // Refresh plans list
        await dispatch(adminGetAllFeatures({ page, searchTerm: debouncedSearchTerm }));
      } catch (error) {
        console.error(error);
        toast.error("Une erreur est survenue lors de la suppression.");
        setModalState((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  const handleTableAction = (action: string, item: PlanFeature) => {
    if (action === "delete") {
      handleDeleteModalOpen(item);
    } else if (action === "view") {
      handleViewModalOpen(item);
    }
  };

  const columns = [
    {
      key: "date",
      label: "Date",
      render: (item: PlanFeature) => <span>{formatDate(item.createdAt)}</span>,
    },
    {
      key: "description",
      label: "Description",
      render: (item: PlanFeature) => <span>{item.description}</span>,
    },

    {
      key: "actions",
      label: "Actions",
    },
  ];

  return (
    <div className="min-h-screen nc-AccountPage bg-gray-50 dark:bg-gray-900" data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <div className="container px-4 py-8 mx-auto">
          <div className="flex flex-col justify-between gap-4 mb-10 sm:flex-row sm:items-center">
            <div className="flex items-center space-x-3">
              <ListCollapse className="text-3xl text-blue-500 dark:text-blue-400" aria-hidden="true" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Caractéristiques</h2>
            </div>
            <div className="flex items-center w-full space-x-4 sm:w-auto">
              <ButtonPrimary
                onClick={() => setModalState({ ...modalState, isAddModalOpen: true })}
                className="btn-primary-gradient"
                sizeClass="px-4 py-2"
              >
                Ajouter caractéristiques
              </ButtonPrimary>
              <div className="relative w-full max-w-lg">
                <Input
                  type="text"
                  placeholder="Rechercher par nom..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full py-3 pl-11 pr-10 text-sm font-medium text-gray-900 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:shadow-md transition-all duration-300 ease-in-out focus:scale-[1.01]"
                  aria-label="Search categories"
                />
                <MagnifyingGlassIcon
                  className={`absolute w-5 h-5 top-1/2 transform -translate-y-1/2 left-3 transition-colors ${
                    searchTerm ? "text-blue-500 dark:text-blue-400" : "text-gray-400 dark:text-gray-500"
                  }`}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute w-5 h-5 p-0.5 top-1/2 transform -translate-y-1/2 right-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMarkIcon />
                  </button>
                )}
              </div>
            </div>
          </div>

          {isSearchLoading && <Loading />}

          {loading === "pending" && !isSearchLoading && <Loading />}

          {loading !== "pending" && (
            <>
              {modalState.isDeleteModalOpen && (
                <DeleteModal
                  open={modalState.isDeleteModalOpen}
                  handleClose={handleModalClose}
                  handleDelete={handleDeleteConfirm}
                  category={{
                    id: modalState.selectedItem?.id || 0,
                    designation: modalState.selectedItem?.description || "",
                  }}
                />
              )}
              {modalState.isAddModalOpen && (
                <PlanFeatureDialogForm
                  open={modalState.isAddModalOpen}
                  handleClose={() =>
                    setModalState({
                      ...modalState,
                      isAddModalOpen: false,
                      selectedItem: null,
                      isDeleteModalOpen: false,
                    })
                  }
                  isLoading={modalState.loading}
                  addfn={addFeature}
                  addfnTitle="Ajouter caractéristiques"
                  onSubmitSuccess={() => {
                    dispatch(adminGetAllFeatures({ page: 1, searchTerm: "" }));
                  }}
                />
              )}

              {modalState.isViewModalOpen && (
                <PlanFeatureDetail
                  open={modalState.isViewModalOpen}
                  handleClose={handleModalClose}
                  data={modalState.selectedItem}
                />
              )}
              <DynamicTable
                columns={columns}
                data={featuresList}
                totalItems={totalCount || 0}
                currentPage={currentPage || 1}
                onPageChange={handlePageChange}
                onAction={handleTableAction}
                totalPages={totalPages}
                availableActions={["view", "delete"]}
              />
            </>
          )}
        </div>
      </DashboardMenu>
    </div>
  );
};

export default PlanFeatures;
